/**
 * UpdateNotice — TEST 2.18 (Alex's decision of 2026-09-24): a tab that has fallen behind a
 * deploy says so and offers a reload.
 *
 * ★★ WHEN IT CHECKS: when the tab becomes visible, and every five minutes while it stays
 * visible. A hidden tab checks nothing. Each check is one no-store GET of `/VERSION`
 * through readServedVersion, which only reads.
 *
 * ★★ WHEN IT SHOWS: the check says 'stale', or the at-load check in main.jsx ended in
 * 'gave-up' or 'guard-unavailable' (it announces that on STALE_LOAD_EVENT).
 *
 * ★★★ IT NEVER RELOADS ON ITS OWN. deleteCachesAndReload is reached only from the button's
 * onClick. scripts/check-stale-build.mjs SB-7 holds it.
 *
 * ★★ "NOT NOW" HIDES IT UNTIL THE NEXT PAGE LOAD (Alex answer 14, 2026-09-27; PROD-256).
 * Before P19 it hid the bar only until the next check, so the bar came back five minutes
 * later, or on the next tab switch. The dismissal is a ref: it lives exactly one page load,
 * because this component is mounted once, in main.jsx, with no key. No storage, so a reload
 * or a relaunch shows the bar again. A background resume or a bfcache restore is not a page
 * load (P19 decision 1) and keeps it hidden. While it is set, no check runs at all.
 * scripts/check-stale-build.mjs SB-13 runs this component and holds it.
 *
 * ★★★ AND IT NEVER UNREGISTERS THE SERVICE WORKER (P8 review R1 = (b)). That would end the
 * patient's Web Push subscription on every tap, and nothing subscribes again on its own.
 * Deleting the caches and reloading is enough, because public/sw.js is network-first.
 * scripts/check-stale-build.mjs SB-9 holds it, through any helper.
 *
 * A bottom bar that pays the bottom safe-area inset; it never covers the screen.
 */
import { useEffect, useRef, useState } from 'react'
import { useI18n } from '../i18n'
import { readServedVersion, deleteCachesAndReload, STALE_LOAD_EVENT } from '../utils/staleBuild'

const CHECK_EVERY_MS = 5 * 60 * 1000

export default function UpdateNotice() {
  const { t } = useI18n()
  const [shown, setShown] = useState(false)
  const dismissed = useRef(false)

  useEffect(() => {
    let alive = true
    const check = async () => {
      if (dismissed.current) return
      if (document.visibilityState !== 'visible') return
      const { state } = await readServedVersion()
      if (alive && !dismissed.current && state === 'stale') setShown(true)
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check()
    }
    const onStaleAtLoad = () => { if (alive && !dismissed.current) setShown(true) }
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener(STALE_LOAD_EVENT, onStaleAtLoad)
    const timer = setInterval(check, CHECK_EVERY_MS)
    return () => {
      alive = false
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener(STALE_LOAD_EVENT, onStaleAtLoad)
      clearInterval(timer)
    }
  }, [])

  if (!shown) return null

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pt-3 bg-[#15354F] text-white shadow-lg"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
    >
      <div className="max-w-md mx-auto flex items-center gap-3">
        <p className="flex-1 text-sm leading-snug">{t('updateNotice.message')}</p>
        <button
          type="button"
          onClick={() => { dismissed.current = true; setShown(false) }}
          className="text-sm text-white/80 px-2 py-2"
        >
          {t('updateNotice.dismiss')}
        </button>
        <button
          type="button"
          onClick={() => { deleteCachesAndReload() }}
          className="text-sm font-semibold bg-[#46A8F9] text-white rounded-lg px-3 py-2"
        >
          {t('updateNotice.button')}
        </button>
      </div>
    </div>
  )
}
